import { Add, AddOutlined, EditOutlined, LockOutlined } from '@mui/icons-material';
import { Button, Chip, CircularProgress, MenuItem, Stack, TextField, Tooltip } from '@mui/material';
import React from 'react';

import PdfPreviewDialog from 'components/v2/common/pdfPreviewDialog';
import Result from 'components/v2/common/Result';
import { SectionCard } from 'components/v2/common/sectionCard';
import MilestonesTable from 'views/v2/Dashboards-B2B/components/backOfficeFlow/agreementMilestones/components/milestonesTable';

import { FEATURE } from 'constant/features';
import type { ApplicationMilestonesEditor } from 'hooks/useApplicationMilestonesEditor';
import useHasFeature from 'hooks/useHasFeature';
import useTranslate from 'hooks/useTranslate';
import { currencyOptions } from 'utils/currencyOptions';

interface OfferSectionProps {
    locked: boolean;
    editor: ApplicationMilestonesEditor;
}

export const OfferSection: React.FC<OfferSectionProps> = ({ locked, editor }) => {
    const { translate } = useTranslate();
    const hasFinancials = useHasFeature(FEATURE.FINANCIALS);
    const hasOffer = editor.milestones.length > 0;
    const showEmpty = !editor.loading && !hasOffer && !editor.editMode;

    const action = (() => {
        if (locked) {
            return (
                <Tooltip title={translate('offerLockedDescription')}>
                    <Chip
                        size="small"
                        icon={<LockOutlined sx={{ fontSize: 16 }} />}
                        label={translate('locked')}
                        variant="outlined"
                    />
                </Tooltip>
            );
        }
        if (editor.editMode) {
            return null;
        }
        return (
            <Button
                size="small"
                variant={hasOffer ? 'outlined' : 'contained'}
                startIcon={hasOffer ? <EditOutlined /> : <AddOutlined />}
                disabled={editor.loading}
                onClick={editor.startEdit}
                sx={{ textTransform: 'none' }}
            >
                {translate(hasOffer ? 'editOffer' : 'createOffer')}
            </Button>
        );
    })();

    return (
        <SectionCard title={translate('retainerStepOffer')} actions={action}>
            {showEmpty ? (
                <Result
                    title={translate('noOfferYet')}
                    description={translate('noOfferYetDescription')}
                    sx={{ py: 3 }}
                />
            ) : (
                <MilestonesTable
                    milestones={editor.milestones}
                    loading={editor.loading}
                    showInvoiceActions={hasFinancials}
                    canEditPaymentStatus
                    canEditMilestones={editor.editMode && !locked}
                    canEditStatus
                    showStatusColumns
                    saving={editor.saving}
                    statusUpdating={editor.statusUpdating}
                    pendingAction={editor.pendingAction}
                    onInvoiceAction={editor.handleInvoiceAction}
                    onStatusChange={editor.handleStatusChange}
                    onMilestoneFieldChange={editor.handleMilestoneFieldChange}
                    onDeleteMilestone={editor.handleDeleteMilestone}
                />
            )}

            {editor.editMode && !locked ? (
                <Stack
                    direction="row"
                    spacing={1.5}
                    useFlexGap
                    sx={{
                        mt: 1.5,
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                    }}
                >
                    <Button
                        size="small"
                        variant="text"
                        startIcon={<Add />}
                        disabled={editor.saving}
                        onClick={editor.addMilestone}
                        sx={{ textTransform: 'none', mr: 'auto' }}
                    >
                        {translate('addMilestone')}
                    </Button>
                    <TextField
                        select
                        size="small"
                        label={translate('currency')}
                        value={editor.currency}
                        disabled={editor.saving}
                        onChange={(event) => editor.setCurrency(event.target.value)}
                        sx={{ width: 110 }}
                    >
                        {currencyOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </TextField>
                    <Button
                        variant="outlined"
                        disabled={editor.saving}
                        onClick={editor.cancelEdit}
                        sx={{ textTransform: 'none' }}
                    >
                        {translate('cancel')}
                    </Button>
                    <Button
                        variant="contained"
                        disabled={
                            editor.saving || editor.hasEditErrors || editor.milestones.length === 0
                        }
                        onClick={() => void editor.save()}
                        sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
                    >
                        {editor.saving ? (
                            <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} />
                        ) : null}
                        {translate('saveOffer')}
                    </Button>
                </Stack>
            ) : null}

            {editor.invoicePreview ? (
                <PdfPreviewDialog
                    title={`${translate('invoicePreview')} #${editor.invoicePreview.milestoneId}`}
                    file={editor.invoicePreview.file}
                    downloadFileName={`invoice-${editor.invoicePreview.milestoneId}.pdf`}
                    onClose={editor.closeInvoicePreview}
                />
            ) : null}
        </SectionCard>
    );
};
